import React from "react";
import axios from "axios";
import { AlertColor, Button, IconButton, InputAdornment, Link, TextField, Tooltip, useMediaQuery} from "@mui/material";
import { Add, Key, Search } from "@mui/icons-material";
import { DataGridPro, GridCellParams, GridColumns, GridRowParams, GridToolbarContainer } from '@mui/x-data-grid-pro';
import Title from "../../component/Title";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { CustomTablePagination } from "../../component/CustomPagination";
import { Staff } from "./StaffAccessories";
import { StaffSingle, StaffSingleHandle } from "./StaffSingle";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { useNavigate } from "react-router-dom";
import { OpenMode } from "../../../common/Enums";
import { StaffPassword, StaffPasswordHandle } from "./StaffPassword";

export function StaffList(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

	const singleModalRef = React.useRef<StaffSingleHandle>(null);

	const passwordModalRef = React.useRef<StaffPasswordHandle>(null);

	const navigate = useNavigate();

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');
  
	const [gridData, setGridData] = React.useState<Staff[]>([]);
	const [selectedStaffId, setSelectedStaffId] = React.useState<number>(0);

    const [searchData, setSearchData] = React.useState<any>({
        pageSize: 15,
    });

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	function CustomToolbar() {
		const [inputText, setInputText] = React.useState("");
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
				<TextField className={style.ModalContentField}
					style={{ minWidth: '100px', width: '100%' }}
					label="Staff Name / Login Name"
					value={inputText}
					onChange={(e) => setInputText(e.target.value)}
					onKeyDown={(e) => KeyPressed(e.key, inputText)}
					InputProps={{
						endAdornment: (
						  <InputAdornment position="end">
							<IconButton onClick={() => {
								SearchStaff(inputText);
							  }}><Search />
							</IconButton>
						  </InputAdornment>
						),
					  }}   
				/>
				<div style={{ minWidth: "20px" }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "4px" }} startIcon={<Add />} onClick={() => NewObject()}>Add</Button>
				<div style={{ width: "10px", minWidth: "10px" }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "4px" }} startIcon={<Key />} disabled={selectedStaffId==0}
					onClick={() => ChangePassword()}>Password</Button>
				<div style={{ flexGrow: 1, minWidth: "10px" }}></div>
			</GridToolbarContainer>
		);
	}

	function ReloadStaffs(staff: Staff)
	{
		let staffList = [...gridData];
		if (gridData.find(x => x.staffId == staff.staffId))
			staffList[gridData.findIndex(x => x.staffId == staff.staffId)] = staff;
		else
			staffList.push(staff);

		setGridData(staffList);
	}

    function KeyPressed(key: string, inputText: string) {
		if(key === 'Enter'){
			SearchStaff(inputText);
		}      
	}

	useConstructor(() => Initialise())
    function Initialise() {
		renewToken();
		setSelectedStaffId(0);
        SearchStaff("");
    }

	function SearchStaff(inputText: string) {
		setLoading(true);

		const params = { searchString: inputText };
		axios.get<Staff[]>('Api/V1/Staff/Search', {
		  params,
		  headers: {
			'Content-Encoding': 'gzip'
		  }
		})
			.then((result) => {
				result.data.forEach(i => {
				i.region = i.region;
				});
				setGridData(result.data);
				setLoading(false);
			},
				(error) => {
				setGridData([]);
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	//Grid columns
	const columns: GridColumns = [
		{ field: 'staffName', headerName: 'Staff Name', flex: 4, type: 'string' },
		{ field: 'loginName', headerName: 'Login Name', flex: 4, type: 'string' },
		{ field: 'region', headerName: 'Region', flex: 1, type: 'string' }
	];
	  
	function NewObject() {
		let staff: Staff = {
			staffId: 0,
			staffName: "",
			loginName: "",
			title: "",
			region: "",
			isActive: false
		}
		singleModalRef.current?.Initialise(OpenMode.NEW, staff);
	}
	  
	function ChangePassword() {
		if (selectedStaffId && selectedStaffId>0)
			passwordModalRef.current?.Initialise(selectedStaffId);
	}

	function EditObject(params: GridRowParams<any>) {
        setLoading(true);
		setSelectedStaffId(params.row.staffId);
		let staffId = params.row.staffId;
		axios.get<Staff>('Api/V1/Staff/' + staffId.toString(), {
			headers: {
			  'Content-Encoding': 'gzip'
			}
		  })
			.then((result) => {
				singleModalRef.current?.Initialise(OpenMode.EDIT, result.data);
				setLoading(false);
			},
			  (error) => {
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			}
			)
			.catch((error) => {
			  setLoading(false);
			  console.log(error);
			  showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	return (
        <div className={style.PageMain}>
			<Title pageTitle="Staff Management" />
			<div className={style.PageContent}>
				<DataGridPro
					getRowId={(row) => row.staffId}
					rows={gridData}
					columns={columns}
					pageSize={searchData.pageSize}
					onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
					pagination
					rowsPerPageOptions={[15, 30, 50, 100]}
					loading={loading}
					components={{
						Toolbar: CustomToolbar,
						Footer: CustomTablePagination
					}}
					componentsProps={{
						footer: { rowsPerPageOptions: [15, 30, 50, 100] }
					}}
					headerHeight={tinyScreen ? 30 : (smallScreen ? 34 : 38)}
					rowHeight={tinyScreen ? 22 : (smallScreen ? 28 : 34)}
					onRowClick={(params)=>setSelectedStaffId(params.row.staffId)}
					onRowDoubleClick={EditObject}
				/>
			</div>

			<StaffSingle
				ref={singleModalRef}
				onClose={(saved: boolean, staff: Staff) => {
					ReloadStaffs(staff);
				}} 
				customers={[]} 
				endUsers={[]} 
				regions={props.regions} 
				sales={[]} 
				deliveryMethods={[]} 
				paymentTerms={[]} 
				currencies={[]}			
			/>

			<StaffPassword
				ref={passwordModalRef}
				onClose={() => {}} 
			/>

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

